import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

import type {
  AcceptInviteResponse,
  LoginResponse,
  MeResponse,
  SessionUser,
  SignupRequest,
  SignupResponse,
} from '@forbiz/contracts'

import { api, ApiError, setUnauthorizedHandler } from './api.js'

/**
 * 세션 상태. web과 admin이 공유한다.
 *
 * 역할별 접근 제어(admin은 운영자만 등)는 여기서 하지 않는다.
 * 각 앱이 라우팅 단계에서 currentUser.role로 가른다. 이 훅은 세션만 관리한다.
 */
interface AuthState {
  user: SessionUser | null
  /** 최초 세션 확인 중. 이 동안은 화면을 그리지 않는다. */
  loading: boolean
  login: (email: string, password: string) => Promise<SessionUser>
  logout: () => Promise<void>
  /**
   * 초대 수락. 비밀번호를 설정하면 서버가 계정을 활성화하고 세션 쿠키를 심는다.
   * 응답의 user로 컨텍스트를 채운다 — accept 후 login을 또 부르지 않기 위해서다.
   */
  acceptInvite: (token: string, password: string) => Promise<SessionUser>
  /**
   * 셀프 회원가입(BIZ-67). 초대 없이 주체별 계정을 만든다. 서버가 계정을 활성화하고
   * 세션 쿠키를 심으며 응답의 user로 컨텍스트를 채운다(자동 로그인).
   */
  signup: (input: SignupRequest) => Promise<SessionUser>
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * 세션 확인(최초 /api/auth/me) 중에 보여줄 전체 화면 로더.
 * 예전엔 이 동안 아무것도 안 그려(null) 새로고침 시 화면이 사라진 것처럼 보였다 —
 * 특히 개발 중 API 재시작으로 me 응답이 지연될 때. 스피너로 "불러오는 중"을 알린다.
 */
function AuthLoading() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--color-bg-page)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" style={{ animation: 'forbiz-auth-spin 0.8s linear infinite' }} aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-border-subtle)" strokeWidth="2.5" />
          <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-muted)' }}>불러오는 중…</span>
      </div>
      <style>{'@keyframes forbiz-auth-spin { to { transform: rotate(360deg) } }'}</style>
    </div>
  )
}

/**
 * 서버에 연결하지 못했을 때(부트스트랩 재시도 소진) 보여주는 화면(BIZ-52).
 * 세션이 만료된 게 아니라 잠깐 불통일 수 있으므로 로그인 화면 대신 "다시 시도"를 준다.
 */
function AuthUnreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--color-bg-page)', padding: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', maxWidth: 320 }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 8v5M12 16h.01" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="9" stroke="var(--color-border-subtle)" strokeWidth="2" />
        </svg>
        <div style={{ fontWeight: 600 }}>서버에 연결할 수 없습니다</div>
        <div style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          일시적인 연결 문제일 수 있습니다. 로그인은 풀리지 않았습니다 — 잠시 후 다시 시도해 주세요.
        </div>
        <button type="button" data-variant="primary" onClick={onRetry} style={{ marginTop: 4 }}>
          다시 시도
        </button>
      </div>
    </div>
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  // 부트스트랩이 서버 불통으로 실패한 상태. 로그인 화면 대신 재시도 화면을 보인다(BIZ-52).
  const [unreachable, setUnreachable] = useState(false)

  // 401 핸들러가 "지금 로그인돼 있나"를 재등록 없이 읽도록 ref로 최신 user를 들고 있는다.
  const userRef = useRef<SessionUser | null>(user)
  userRef.current = user

  useEffect(() => {
    // 세션이 중간에 만료돼 어떤 요청이든 401을 받으면 user를 비운다 →
    // RequireAuth가 (현재 경로를 기억한 채) 로그인 화면으로 보낸다.
    // 이미 로그아웃 상태(부트스트랩·로그인 화면의 401 등)면 아무것도 안 한다 —
    // 억울한 리다이렉트·중복 상태 변경을 막는다(BIZ-30).
    setUnauthorizedHandler(() => {
      if (userRef.current) setUser(null)
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  /**
   * 최초 세션 확인(/api/auth/me). 실패를 구분한다(BIZ-52):
   *  - 401 → 진짜 미인증 → user를 비우고 로그인 화면으로.
   *  - 일시적 실패(네트워크·5xx, 예: dev 서버 재시작) → 짧은 백오프로 재시도한다.
   *    세션은 살아 있을 수 있으므로 함부로 로그아웃시키지 않는다. 재시도 동안은 로딩 스피너 유지.
   *  - 재시도를 모두 소진 → 서버 불통으로 보고 재시도 화면을 보인다(로그인으로 튕기지 않는다).
   */
  const bootstrap = useCallback(async () => {
    setLoading(true)
    setUnreachable(false)
    const MAX_ATTEMPTS = 4
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const res = await api.get<MeResponse>('/api/auth/me')
        setUser(res.user)
        setLoading(false)
        return
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null)
          setLoading(false)
          return
        }
        // 일시적 실패: 점증 백오프(0.5s·1s·1.5s) 후 재시도.
        if (attempt < MAX_ATTEMPTS - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)))
        }
      }
    }
    setUnreachable(true)
    setLoading(false)
  }, [])

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/api/auth/login', { email, password })
    setUser(res.user)
    return res.user
  }, [])

  const logout = useCallback(async () => {
    // 서버 세션 폐기는 best-effort. 네트워크가 실패해도 클라 상태는 반드시 비운다.
    // 안 그러면 LoginForm의 역할 거부 경로에서 logout이 throw할 때 비운영자
    // user가 컨텍스트에 남는다(테스트 에이전트 지적).
    try {
      await api.post('/api/auth/logout')
    } finally {
      setUser(null)
    }
  }, [])

  const acceptInvite = useCallback(async (token: string, password: string) => {
    const res = await api.post<AcceptInviteResponse>('/api/auth/invite/accept', { token, password })
    setUser(res.user)
    return res.user
  }, [])

  const signup = useCallback(async (input: SignupRequest) => {
    const res = await api.post<SignupResponse>('/api/auth/signup', input)
    setUser(res.user)
    return res.user
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, acceptInvite, signup }}>
      {loading ? <AuthLoading /> : unreachable ? <AuthUnreachable onRetry={() => void bootstrap()} /> : children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
